/**
 * The old panel's order-stage catalog, transcribed 1-to-1 from
 * `merchant-panel/src/assets/data/order_stages.ts`.
 *
 * The backend reports a numeric `stage` on every order; the badge renders that
 * stage's label and color. Colors are NOT repeated here — each stage reads
 * `--lg-stage-<n>` from the legacy token layer, so this file stays a pure
 * number → label map and the palette lives with the other legacy tokens.
 *
 * Gaps are intentional: stage 30 does not exist in the backend enum, and 31
 * ("equipment blocked") sits past the contiguous run. The lookup is a Map, not
 * an array index, so the gaps cost nothing and an unknown stage degrades to a
 * neutral badge instead of throwing.
 */

/** i18n keys live under the `legacy` namespace: `stages.<key>`. */
export interface LegacyOrderStage {
  number: number;
  labelKey: string;
}

export const LEGACY_ORDER_STAGES: readonly LegacyOrderStage[] = [
  { number: 1, labelKey: 'stages.new' },
  { number: 2, labelKey: 'stages.estimating' },
  { number: 3, labelKey: 'stages.ready_for_approval' },
  { number: 4, labelKey: 'stages.scheduled' },
  { number: 5, labelKey: 'stages.performer_look_up' },
  { number: 6, labelKey: 'stages.performer_draft' },
  { number: 7, labelKey: 'stages.performer_found' },
  { number: 8, labelKey: 'stages.pick_up_arrived' },
  { number: 9, labelKey: 'stages.ready_for_pick_up_confirmation' },
  { number: 10, labelKey: 'stages.picked_up' },
  { number: 11, labelKey: 'stages.delivery_arrived' },
  { number: 12, labelKey: 'stages.ready_for_delivery_confirmation' },
  { number: 13, labelKey: 'stages.pay_waiting' },
  { number: 14, labelKey: 'stages.delivered' },
  { number: 15, labelKey: 'stages.delivered_finish' },
  { number: 16, labelKey: 'stages.returning' },
  { number: 17, labelKey: 'stages.return_arrived' },
  { number: 18, labelKey: 'stages.ready_for_return_confirmation' },
  { number: 19, labelKey: 'stages.returned' },
  { number: 20, labelKey: 'stages.returned_finish' },
  { number: 21, labelKey: 'stages.cancelled_by_taxi' },
  { number: 22, labelKey: 'stages.cancelled' },
  { number: 23, labelKey: 'stages.cancelled_with_payment' },
  { number: 24, labelKey: 'stages.cancelled_with_items_on_hand' },
  { number: 25, labelKey: 'stages.failed' },
  { number: 26, labelKey: 'stages.estimating_failed' },
  { number: 27, labelKey: 'stages.performer_not_found' },
  { number: 28, labelKey: 'stages.cancelled_out_of_zone' },
  { number: 29, labelKey: 'stages.not_fit' },
  { number: 31, labelKey: 'stages.equipment_blocked' },
] as const;

const STAGE_BY_NUMBER = new Map(LEGACY_ORDER_STAGES.map((s) => [s.number, s]));

export const findLegacyOrderStage = (stage: number): LegacyOrderStage | undefined =>
  STAGE_BY_NUMBER.get(stage);

/**
 * The status filter pills above the table, in the old panel's exact order.
 *
 * `countKey` selects the pill's badge count from the response's `count_info`;
 * `param` is the query flag the old list endpoint expects. Both are needed
 * because the two vocabularies disagree — the "not paid" pill counts
 * `not_paid_count` but filters with `is_paid=false`, and `debt_count` filters
 * with `is_courier_debt=false`. `dotVar` is the legacy CSS variable for the
 * pill's leading dot.
 */
export interface LegacyStatusTab {
  /** Stable id, also the URL `status` value. Matches the old app's constants. */
  id: string;
  labelKey: string;
  countKey: string;
  dotVar: string;
  /** Query param + value the list endpoint filters by. `null` for "all". */
  filter: { param: string; value: boolean } | null;
}

export const LEGACY_STATUS_TABS: readonly LegacyStatusTab[] = [
  {
    id: 'ALL',
    labelKey: 'stages.all',
    countKey: 'total',
    dotVar: '--lg-fg',
    filter: null,
  },
  {
    id: 'SHCHEDULED', // (sic) — the old app's spelling; kept so URLs match.
    labelKey: 'stages.scheduled',
    countKey: 'scheduled',
    dotVar: '--lg-light-blue-500',
    filter: { param: 'is_scheduled', value: true },
  },
  {
    id: 'SEARCH',
    labelKey: 'stages.performer_look_up',
    countKey: 'in_search',
    dotVar: '--lg-orange-600',
    filter: { param: 'is_in_search', value: true },
  },
  {
    id: 'NOT-FOUND',
    labelKey: 'stages.performer_not_found',
    countKey: 'not_found',
    dotVar: '--lg-grey-600',
    filter: { param: 'is_not_found', value: true },
  },
  {
    id: 'CURRENT',
    labelKey: 'stages.current',
    countKey: 'active',
    dotVar: '--lg-violet-600',
    filter: { param: 'is_active', value: true },
  },
  {
    id: 'RETURNED',
    labelKey: 'stages.returned',
    countKey: 'returned',
    dotVar: '--lg-pink-600',
    filter: { param: 'is_returned', value: true },
  },
  {
    id: 'COMPLETED',
    labelKey: 'stages.completed',
    countKey: 'finished',
    dotVar: '--lg-green-600',
    filter: { param: 'is_finished', value: true },
  },
  {
    id: 'CANCELLED',
    labelKey: 'stages.cancelled',
    countKey: 'cancelled',
    dotVar: '--lg-red-600',
    filter: { param: 'is_cancelled', value: true },
  },
  {
    id: 'COURIER_DEBT',
    labelKey: 'stages.debt_count',
    countKey: 'debt_count',
    dotVar: '--lg-yellow-800',
    filter: { param: 'is_courier_debt', value: false },
  },
  {
    id: 'PRODUCT_NOT_PAID',
    labelKey: 'stages.product_not_paid',
    countKey: 'not_paid_count',
    dotVar: '--lg-red-800',
    filter: { param: 'is_paid', value: false },
  },
] as const;

export const LEGACY_STATUS_TAB_IDS = LEGACY_STATUS_TABS.map((t) => t.id) as [
  string,
  ...string[],
];

/** Rows per page — the old list hardcoded 15. */
export const LEGACY_ORDERS_PAGE_SIZE = 15;
